import React, { useState } from 'react';
import { Plus, DollarSign, Trash2, Building2, Wifi, Wrench, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { formatCurrency } from '@/utils/helpers';
import api from '@/utils/api';
import useApiData from '@/hooks/useApiData';
import { toast } from 'sonner';


const CATEGORIAS = [
  { value: 'Aluguel', icon: Building2, color: 'bg-blue-100 text-blue-700' },
  { value: 'Ferramentas', icon: Wrench, color: 'bg-purple-100 text-purple-700' },
  { value: 'Internet', icon: Wifi, color: 'bg-green-100 text-green-700' },
  { value: 'Salários Admin', icon: Users, color: 'bg-orange-100 text-orange-700' },
  { value: 'Outros', icon: DollarSign, color: 'bg-slate-100 text-slate-700' },
];

export default function CustosFixos() {
  const { data: custosData, refresh: refreshCustos } = useApiData('/advanced/custos-fixos');
  const custos = Array.isArray(custosData) ? custosData : [];

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    descricao: '',
    categoria: 'Outros',
    valor_mensal: '',
    recorrente: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        descricao: formData.descricao,
        categoria: formData.categoria,
        valor_mensal: parseFloat(formData.valor_mensal),
        recorrente: formData.recorrente
      };
      await api.post('/advanced/custos-fixos', payload);
      toast.success('Custo fixo cadastrado!');
      setIsDialogOpen(false);
      resetForm();
      refreshCustos();

    } catch (error) {
      console.error('Erro ao cadastrar custo fixo:', error.response?.data || error.message);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao cadastrar custo fixo');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (custoId) => {
    if (!window.confirm('Tem certeza que deseja excluir este custo?')) return;

    try {
      await api.delete(`/advanced/custos-fixos/${custoId}`);
      toast.success('Custo excluído');
      refreshCustos();

    } catch (error) {
      toast.error('Erro ao excluir custo');
    }
  };

  const resetForm = () => {
    setFormData({
      descricao: '',
      categoria: 'Outros',
      valor_mensal: '',
      recorrente: true
    });
  };

  const getCategoriaConfig = (categoria) => {
    return CATEGORIAS.find(c => c.value === categoria) || CATEGORIAS[4];
  };

  const totalCustosMensais = custos
    .filter(c => c.recorrente)
    .reduce((sum, c) => sum + c.valor_mensal, 0);

  const custosPorCategoria = custos.reduce((acc, custo) => {
    const cat = custo.categoria || 'Outros';
    acc[cat] = (acc[cat] || 0) + custo.valor_mensal;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="custos-fixos-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight text-slate-900">Custos Fixos</h1>
          <p className="text-slate-600 mt-1">Gerencie seus custos operacionais mensais</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="new-custo-btn">
              <Plus className="h-4 w-4 mr-2" />
              Novo Custo Fixo
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Novo Custo Fixo</DialogTitle>
              <DialogDescription>Adicione um novo custo fixo operacional</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Descrição *</Label>
                <Input
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder="Ex: Aluguel do escritório"
                  required
                />
              </div>

              <div>
                <Label>Categoria</Label>
                <Select
                  value={formData.categoria}
                  onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Valor Mensal (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.valor_mensal}
                  onChange={(e) => setFormData({ ...formData, valor_mensal: e.target.value })}
                  required
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label>Recorrente</Label>
                  <p className="text-sm text-slate-500">Cobrado todo mês</p>
                </div>
                <Switch
                  checked={formData.recorrente}
                  onCheckedChange={(checked) => setFormData({ ...formData, recorrente: checked })}
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="h-12 w-12 rounded-full bg-error/10 flex items-center justify-center text-error mb-4">
            <DollarSign className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-slate-500 uppercase mb-1">Total Mensal</p>
          <p className="text-3xl font-bold text-slate-900">{formatCurrency(totalCustosMensais)}</p>
        </div>

        {CATEGORIAS.slice(0, 3).map((cat) => {
          const Icon = cat.icon;
          return (
            <div key={cat.value} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
              <div className={`h-12 w-12 rounded-full ${cat.color.replace('text-', 'bg-').replace('700', '100')} flex items-center justify-center mb-4`}>
                <Icon className={`h-6 w-6 ${cat.color.split(' ')[1]}`} />
              </div>
              <p className="text-sm font-medium text-slate-500 uppercase mb-1">{cat.value}</p>
              <p className="text-3xl font-bold text-slate-900">{formatCurrency(custosPorCategoria[cat.value] || 0)}</p>
            </div>
          );
        })}
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50/50 border-b border-slate-100">
              <tr>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Descrição</th>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Categoria</th>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Valor Mensal</th>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Tipo</th>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody>
              {custos.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-slate-500">
                    Nenhum custo fixo cadastrado
                  </td>
                </tr>
              ) : (
                custos.map((custo) => {
                  const catConfig = getCategoriaConfig(custo.categoria);
                  const Icon = catConfig.icon;
                  return (
                    <tr key={custo.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={`h-8 w-8 rounded-full ${catConfig.color.replace('text-', 'bg-').replace('700', '100')} flex items-center justify-center`}>
                            <Icon className={`h-4 w-4 ${catConfig.color.split(' ')[1]}`} />
                          </div>
                          <span className="font-medium text-slate-900">{custo.descricao}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge className={catConfig.color}>
                          {custo.categoria}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm font-mono font-bold text-error">{formatCurrency(custo.valor_mensal)}</td>
                      <td className="p-4">
                        <Badge variant={custo.recorrente ? 'default' : 'outline'}>
                          {custo.recorrente ? 'Recorrente' : 'Avulso'}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(custo.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
